import React, { createContext, useContext, useCallback } from 'react';
import { toast as sonnerToast } from 'sonner';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextType {
  /** Sonner manages its own queue; kept as a stable empty array for compatibility. */
  toasts: Toast[];
  addToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * Toast provider backed by sonner (W5 — single toast system).
 *
 * The public API is kept identical to the legacy custom implementation so the
 * 12 existing consumers (`toast.success/error/info/warning`) work unchanged.
 * The sonner <Toaster /> is mounted once in App.tsx.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const addToast = useCallback((type: ToastType, message: string, duration = 5000) => {
    const fn = sonnerToast[type];
    if (typeof fn === 'function') {
      fn(message, { duration });
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    sonnerToast.dismiss(id);
  }, []);

  const success = useCallback((message: string) => sonnerToast.success(message), []);
  const error = useCallback(
    (message: string) => sonnerToast.error(message, { duration: 8000 }),
    []
  );
  const info = useCallback((message: string) => sonnerToast.info(message), []);
  const warning = useCallback(
    (message: string) => sonnerToast.warning(message, { duration: 6000 }),
    []
  );

  return (
    <ToastContext.Provider
      value={{ toasts: [], addToast, removeToast, success, error, info, warning }}
    >
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
